import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import dayjs from 'dayjs';
import { useState } from 'react';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultCalendarAdvancedOptions from './shared/vault-calendar-advanced-options.tsx';
import VaultTooltipWithLink from './shared/vault-tooltip-with-link.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultDatePicker from './shared/vault-date-picker.tsx';
import { DestUser } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import VaultDataScopeSelect from './shared/vault-data-scope-select.tsx';
import VaultTimezoneSelect from './shared/vault-timezone-select.tsx';
import VaultTermsInput from './shared/vault-terms-input.tsx';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export interface VaultCalendarExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultCalendarExportConfig({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultCalendarExportConfigProps) {
  const theme = useTheme();
  const vaultStartTime = watch(`actions.${day}.actions.${idx}.vaultStartTime`);
  const vaultEndTime = watch(`actions.${day}.actions.${idx}.vaultEndTime`);
  const vaultVersionDate = watch(
    `actions.${day}.actions.${idx}.vaultVersionDate`
  );

  const defaultDateRangeType =
    vaultStartTime || vaultEndTime || vaultVersionDate ? 'custom' : 'all';
  const [dateRangeType, setDateRangeType] =
    useState<string>(defaultDateRangeType);

  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleDateRangeTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = e.target.value;
    setDateRangeType(val);
    if (val === 'all') {
      setValue(`actions.${day}.actions.${idx}.vaultStartTime`, null, {
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.vaultEndTime`, null, {
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.vaultVersionDate`, null, {
        shouldDirty: true,
      });
    }
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="column" sx={{ px: 2, pb: 3 }}>
        <Stack direction="column" gap={3}>
          <DecomNotifyUsersInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
          />

          <DecomReportRecipientInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
            actionType="vaultCalendarExport"
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        </Stack>
        <Divider
          sx={{
            mb: 2,
            mt: 1.5,
            borderColor: theme.palette.semantic.outline.light,
          }}
        />

        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={4}>
            <VaultDataScopeSelect day={day} idx={idx} control={control} />
          </Stack>

          <Stack direction="row" gap={4}>
            <VaultTimezoneSelect day={day} idx={idx} control={control} />
          </Stack>

          <Box>
            <RadioGroup
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">All events up to now</Typography>
                }
              />
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">Custom Date Range</Typography>
                }
              />
            </RadioGroup>
          </Box>

          {dateRangeType === 'custom' && (
            <Stack direction="row" gap={4} flexWrap="wrap">
              <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Event Date (optional)
                  </Typography>
                  <Tooltip
                    title="Only events within this date range will be exported."
                    arrow
                  >
                    <InfoOutlined
                      fontSize="small"
                      color="action"
                      sx={{ mb: 0.5 }}
                    />
                  </Tooltip>
                </Stack>
                <Stack direction="row" gap={2}>
                  <VaultDatePicker
                    day={day}
                    idx={idx}
                    name={`actions.${day}.actions.${idx}.vaultStartTime`}
                    label="Start Date"
                    control={control}
                    errors={errors}
                    maxDate={
                      vaultEndTime && dayjs(vaultEndTime).isValid()
                        ? dayjs(vaultEndTime)
                        : undefined
                    }
                  />
                  <VaultDatePicker
                    day={day}
                    idx={idx}
                    name={`actions.${day}.actions.${idx}.vaultEndTime`}
                    label="End Date"
                    control={control}
                    errors={errors}
                    minDate={
                      vaultStartTime && dayjs(vaultStartTime).isValid()
                        ? dayjs(vaultStartTime)
                        : undefined
                    }
                  />
                </Stack>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Version Date (optional)
                  </Typography>
                  <VaultTooltipWithLink
                    preset="calendarEventVersioning"
                    sxIcon={{ mb: 0.5 }}
                  />
                </Stack>
                <VaultDatePicker
                  day={day}
                  idx={idx}
                  name={`actions.${day}.actions.${idx}.vaultVersionDate`}
                  label="Version Date"
                  control={control}
                  errors={errors}
                />
              </Box>
            </Stack>
          )}

          <VaultTermsInput
            day={day}
            idx={idx}
            control={control}
            tooltipPreset="calendarQueryTerms"
            placeholder="Keywords contained in the title, description, organizers, guests, locations or rooms"
            helperText="Enter words or phrases in quotation marks"
          />

          <VaultCalendarAdvancedOptions day={day} idx={idx} control={control} />
        </Stack>
      </Stack>
    </Stack>
  );
}
